import { Button, Card, Empty, Segmented, Spin } from "antd";
import { useState } from "react";
import {
  useAnalyzeAiBrandFit,
  useAnalyzeAiResult,
  useUpdateAiBrandFit,
} from "../../hooks/useInfluencer";

const REPORT_TITLE_CLS = "text-[18px] font-semibold mb-6";
const REPORT_BODY_CLS =
  "whitespace-pre-line text-[#262626] text-[14px] md:text-[16px] leading-[1.85] border-l-[3px] border-[#F9A640] p-4 rounded-lg bg-[#FAFAFA]";

const ANALYSIS_SECTIONS = [
  { key: "executive_snapshot", title: "핵심 요약" },
  { key: "performance_analysis", title: "퍼포먼스 분석" },
  { key: "audience", title: "오디언스 분석" },
  {
    key: "brand_fit_and_collaboration_potential",
    title: "콘텐츠 핏 / 브랜드 핏",
  },
  { key: "actionable_recommendations", title: "실행 전략" },
  { key: "vision", title: "방향성" },
];

export const AiAnalysisReport = ({ data }) => {
  if (!data) return null;

  return (
    <div className="space-y-8">
      {ANALYSIS_SECTIONS.map(({ key, title }) => (
        <div key={key}>
          <div className={REPORT_TITLE_CLS}>{title}</div>
          <p className={REPORT_BODY_CLS}>{data[key]}</p>
        </div>
      ))}
    </div>
  );
};

export const CreatorAiAnalysisPanel = ({
  uniqueId,
  aiAnalysisData,
  isVisible = true,
}) => {
  const { data, isLoading } = useAnalyzeAiResult({
    unique_id: uniqueId,
    enabled: isVisible && !aiAnalysisData,
  });

  if (!isVisible) return null;

  const renderCreatorAnalysis = () => {
    if (aiAnalysisData || data) {
      return (
        <Card
          title={
            <span className="text-[18px] font-semibold">AI 분석 결과</span>
          }
          className="rounded-xl border border-[#E8E8E8]"
          styles={{ body: { padding: "24px" } }}
        >
          <AiAnalysisReport data={aiAnalysisData || data} />
        </Card>
      );
    }

    if (isLoading) {
      return (
        <div className="py-16 flex flex-col items-center justify-center gap-4">
          <Spin size="large" />
          <div className="text-[16px] text-[#666] flex flex-col items-center">
            <span className="font-medium">크리에이터 분석 중 입니다.</span>
            <span>잠시만 기다려주세요.</span>
          </div>
        </div>
      );
    }

    return null;
  };

  return renderCreatorAnalysis();
};

export const InfluencerAiAnalysis = ({
  uniqueId,
  aiAnalysisData,
  isVisible,
}) => {
  const [selectedView, setSelectedView] = useState("creator-analysis");

  const brandFitEnabled =
    isVisible && selectedView === "brand-campaign-fit" && !!uniqueId;
  const {
    data: brandFitData,
    isLoading: isBrandFitLoading,
    isFetching: isBrandFitFetching,
  } = useAnalyzeAiBrandFit({
    unique_id: uniqueId,
    enabled: brandFitEnabled,
  });

  const { mutate: updateBrandFit, isPending: isUpdateBrandFitPending } =
    useUpdateAiBrandFit();

  // 브랜드 핏 관련 로딩 상태
  const isBrandFitLoadingAll =
    isBrandFitLoading || isBrandFitFetching || isUpdateBrandFitPending;

  const renderBrandCampaignFit = () => {
    if (isBrandFitLoadingAll) {
      return (
        <div className="py-16 flex flex-col items-center justify-center gap-4">
          <Spin size="large" />
          <div className="text-[16px] text-[#666] flex flex-col items-center">
            <span className="font-medium">
              브랜드 · 캠페인 핏 분석을 불러오는 중입니다.
            </span>
            <span>잠시만 기다려주세요.</span>
          </div>
        </div>
      );
    }

    if (brandFitData) {
      return (
        <Card
          title={
            <span className="text-[18px] font-semibold">
              브랜드 · 캠페인 핏 AI 분석
            </span>
          }
          className="rounded-xl border border-[#E8E8E8]"
          styles={{ body: { padding: "24px" } }}
        >
          <AiAnalysisReport data={brandFitData} />
        </Card>
      );
    }

    return (
      <Empty
        description={
          <div>
            <p>등록된 캠페인이 없어 핏을 분석할 수 없습니다.</p>
            <p>캠페인을 등록한 후 다시 시도해 주세요.</p>
          </div>
        }
        styles={{
          root: { padding: "48px 0" },
          image: { height: 80 },
        }}
      />
    );
  };

  return (
    <div className={isVisible ? undefined : "hidden"}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <Segmented
          value={selectedView}
          onChange={setSelectedView}
          options={[
            { label: "크리에이터 분석", value: "creator-analysis" },
            { label: "브랜드 · 캠페인 핏", value: "brand-campaign-fit" },
          ]}
        />
        {selectedView === "brand-campaign-fit" ? (
          <Button
            type="default"
            loading={isBrandFitLoadingAll}
            disabled={!uniqueId}
            onClick={() => uniqueId && updateBrandFit({ unique_id: uniqueId })}
            style={{ borderColor: "#F9A43F", color: "#F9A43F" }}
          >
            업데이트
          </Button>
        ) : null}
      </div>

      {selectedView === "creator-analysis" ? (
        <CreatorAiAnalysisPanel
          uniqueId={uniqueId}
          aiAnalysisData={aiAnalysisData}
          isVisible={isVisible}
        />
      ) : (
        renderBrandCampaignFit()
      )}
    </div>
  );
};
